/**
 * @fileoverview AdminProfile — Team member "My Profile" page.
 *
 * Sections:
 *   1. Profile Overview — inline-editable displayName/phone, read-only
 *      email/role/last login
 *   2. Assigned Projects — horizontal scroll row of project mini cards
 *   3. My Activity — 15 most recent activity log entries
 *
 * Avatar: click the avatar (or its hover pencil) to upload a new
 * image to Cloudinary (unsigned preset). The resulting URL is
 * written to the Airtable Team record AND the user's Firestore
 * userPreferences doc.
 *
 * @component
 */

import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUserCircle,
  FaPencilAlt,
  FaCheck,
  FaTimes,
  FaFolder,
  FaTasks,
  FaFileAlt,
  FaClipboardList,
  FaHome,
  FaUserPlus,
  FaUserEdit,
  FaCamera,
  FaSpinner,
} from "react-icons/fa";
import AdminPageBanner from "../../components/admin/AdminPageBanner";
import useTeamProfile from "../../hooks/useTeamProfile";
import useUserPreferences from "../../hooks/useUserPreferences";
import { useAdminProjects } from "../../hooks/useAdminProjects";
import { ProjectRow, MobileAccordionItem } from "./AdminProjects";
import { formatDateET } from "../../utils/dateUtils";
import { auth } from "../../firebase";
import "./AdminProjects.css";
import "./AdminProfile.css";

/* ============================================
   CONSTANTS
   ============================================ */

const CLOUDINARY_CLOUD = "dy9lswtxv";
const CLOUDINARY_PRESET = "red_setter_portal";
const CLOUDINARY_FOLDER = "team-avatars";

/**
 * Map an activity log entityType to its display icon.
 * Falls back to a generic file icon for unknown types.
 * @constant {Object}
 */
const ENTITY_ICON = {
  file: <FaFileAlt />,
  photo: <FaCamera />,
  task: <FaTasks />,
  project: <FaHome />,
  client: <FaUserPlus />,
  vendor: <FaUserEdit />,
  changeOrder: <FaClipboardList />,
};

/* ============================================
   HELPERS
   ============================================ */

/**
 * Upload an image to Cloudinary using the unsigned preset for
 * team avatars. Returns the secure (https) URL.
 *
 * @param {File} file - Image file selected by the user
 * @returns {Promise<string>} Cloudinary secure_url
 * @throws {Error} If the upload request fails
 */
async function uploadAvatarToCloudinary(file) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", CLOUDINARY_PRESET);
  formData.append("folder", CLOUDINARY_FOLDER);

  const response = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/upload`,
    { method: "POST", body: formData },
  );

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Avatar upload failed (${response.status}): ${text}`);
  }

  const data = await response.json();
  return data.secure_url;
}

/**
 * Format a timestamp (millis or ISO string) for display in the
 * activity log. Returns an em-dash if the value is falsy.
 * @param {number|string|null} ts
 * @returns {string}
 */
function formatLogTimestamp(ts) {
  if (!ts) return "—";
  try {
    const iso = typeof ts === "number" ? new Date(ts).toISOString() : ts;
    return formatDateET(iso, { includeTime: true });
  } catch (err) {
    return "—";
  }
}

/* ============================================
   SUB-COMPONENTS
   ============================================ */

/**
 * Section wrapper with header, icon, and optional action slot.
 * Matches the structure used by AdminProjectDetail.
 */
const Section = ({ title, icon, subtitle, children }) => (
  <div className="ap-section">
    <div className="ap-section-header">
      <div className="ap-section-title">
        {icon && <span className="ap-section-icon">{icon}</span>}
        <h2>{title}</h2>
      </div>
      {subtitle && <span className="ap-section-subtitle">{subtitle}</span>}
    </div>
    <div className="ap-section-content">{children}</div>
  </div>
);

/**
 * Single inline-editable field row. Renders the current value
 * with a pencil button that swaps the value for an input + save
 * and cancel buttons.
 */
const InlineEditRow = ({ label, value, onSave, disabled, placeholder }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setDraft(value || "");
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setDraft(value || "");
  };
  const save = async () => {
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ap-info-row">
      <label>{label}</label>
      {!editing ? (
        <div className="ap-info-row-value">
          <span className={value ? "" : "ap-muted"}>{value || "Not set"}</span>
          {!disabled && (
            <button
              type="button"
              className="ap-icon-btn"
              onClick={startEdit}
              aria-label={`Edit ${label}`}
            >
              <FaPencilAlt />
            </button>
          )}
        </div>
      ) : (
        <div className="ap-inline-edit">
          <input
            type="text"
            className="ap-inline-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            autoFocus
            disabled={saving}
          />
          <button
            type="button"
            className="ap-save-btn"
            onClick={save}
            disabled={saving}
            aria-label="Save"
          >
            <FaCheck />
          </button>
          <button
            type="button"
            className="ap-cancel-btn"
            onClick={cancel}
            disabled={saving}
            aria-label="Cancel"
          >
            <FaTimes />
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Read-only labeled row (used for email, role, last login).
 */
const ReadOnlyRow = ({ label, value }) => (
  <div className="ap-info-row">
    <label>{label}</label>
    <div className="ap-info-row-value ap-readonly">
      <span className={value ? "" : "ap-muted"}>{value || "—"}</span>
    </div>
  </div>
);

/**
 * Avatar with hover-to-edit overlay. Clicking opens the hidden
 * file input; the parent owns the upload flow.
 */
const AvatarEditor = ({
  avatarUrl,
  uploading,
  onPickFile,
  onRemove,
  inputRef,
  onFileChange,
}) => (
  <div className="ap-avatar-wrap">
    <input
      type="file"
      accept="image/*"
      ref={inputRef}
      onChange={onFileChange}
      className="ap-avatar-input"
    />
    <button
      type="button"
      className="ap-avatar-button"
      onClick={onPickFile}
      disabled={uploading}
      aria-label="Change avatar"
    >
      {avatarUrl ? (
        <img src={avatarUrl} alt="" className="ap-avatar-img" />
      ) : (
        <FaUserCircle className="ap-avatar-placeholder" />
      )}
      <span className="ap-avatar-overlay">
        {uploading ? <FaSpinner className="ap-spin" /> : <FaPencilAlt />}
      </span>
    </button>
    {avatarUrl && !uploading && (
      <button
        type="button"
        className="ap-avatar-remove"
        onClick={onRemove}
        aria-label="Remove avatar"
        title="Remove avatar"
      >
        <FaTimes />
      </button>
    )}
  </div>
);

/* ============================================
   MAIN COMPONENT
   ============================================ */

/**
 * AdminProfile — Team "My Profile" page.
 *
 * @param {Object} props
 * @param {Object} [props.user] - Firebase auth user (passed by router)
 * @param {Object} [props.userData] - Legacy team data (unused — we
 *   fetch directly from useTeamProfile)
 * @returns {JSX.Element}
 */
function AdminProfile({ user, showTestAccounts }) {
  const navigate = useNavigate();
  const {
    profile,
    activityLogs,
    updateProfileAsync,
    isUpdating,
    isLoading: isProfileLoading,
  } = useTeamProfile();
  const { preferences, updatePreferences } = useUserPreferences();

  const { data: allProjects = [] } = useAdminProjects({ showTestAccounts });

  const assignedFullProjects = useMemo(() => {
    const ids = new Set(profile?.assignedProjectIds || []);
    if (ids.size === 0) return [];
    return allProjects.filter((p) => ids.has(p.id));
  }, [allProjects, profile]);

  const [expandedAccordionId, setExpandedAccordionId] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const handleProjectClick = (id) => navigate(`/admin/projects/${id}`);

  const displayTitle =
    profile?.name ||
    profile?.displayName ||
    user?.displayName ||
    user?.email ||
    "My Profile";
  const roleLabel = Array.isArray(profile?.titlePosition)
    ? profile.titlePosition.filter(Boolean).join(" | ")
    : profile?.titlePosition || "";
  const subtitle = "Your Portal Profile and Activity Overview";
  const avatarUrl = profile?.avatarUrl || preferences.avatarUrl || null;

  const lastLogin = auth.currentUser?.metadata?.lastSignInTime
    ? formatDateET(auth.currentUser.metadata.lastSignInTime, {
        includeTime: true,
      })
    : "—";

  const handlePickFile = () => fileInputRef.current?.click();

  const handleRemoveAvatar = async () => {
    if (!window.confirm("Remove your profile picture?")) return;
    try {
      await updateProfileAsync({ avatarUrl: null });
      await updatePreferences({ avatarUrl: null });
    } catch (err) {
      console.error("[AdminProfile] Avatar remove failed:", err);
      alert(`Could not remove avatar: ${err.message || String(err)}`);
    }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingAvatar(true);
    try {
      const url = await uploadAvatarToCloudinary(file);
      await updateProfileAsync({ avatarUrl: url });
      await updatePreferences({ avatarUrl: url });
    } catch (err) {
      console.error("[AdminProfile] Avatar upload failed:", err);
      alert(`Avatar upload failed: ${err.message || String(err)}`);
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const saveDisplayName = async (value) => {
    await updateProfileAsync({ displayName: value });
  };
  const savePhone = async (value) => {
    await updateProfileAsync({ phone: value });
  };

  // While the profile is loading for the first time, show a spinner
  // instead of the page chrome filled with Firebase-auth fallback
  // values (email-as-name, empty role, etc.).
  if (isProfileLoading && !profile) {
    return (
      <div className="ap-container">
        <div className="loading-container">
          <FaSpinner className="admin-dash-spinner" />
          <span className="loading-text">
            Retrieving profile information...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="ap-container">
      <AdminPageBanner title={displayTitle} subtitle={subtitle}>
        <AvatarEditor
          avatarUrl={avatarUrl}
          uploading={uploadingAvatar}
          onPickFile={handlePickFile}
          onRemove={handleRemoveAvatar}
          inputRef={fileInputRef}
          onFileChange={handleFileSelected}
        />
      </AdminPageBanner>

      <Section
        title="Profile Overview"
        icon={<FaUserCircle />}
        subtitle="Edit your display name and phone number (email and role are set by your administrator)"
      >
        <InlineEditRow
          label="Display Name"
          value={profile?.displayName || ""}
          onSave={saveDisplayName}
          disabled={!profile || isUpdating}
          placeholder="Display name"
        />
        <InlineEditRow
          label="Phone"
          value={profile?.phone || ""}
          onSave={savePhone}
          disabled={!profile || isUpdating}
          placeholder="Phone number"
        />
        <ReadOnlyRow label="Email" value={profile?.email || user?.email} />
        <ReadOnlyRow label="Role" value={roleLabel} />
        <ReadOnlyRow label="Last Login" value={lastLogin} />
      </Section>

      <Section
        title="Assigned Projects"
        icon={<FaFolder />}
        subtitle="Click a project to view details and manage your tasks"
      >
        {assignedFullProjects.length === 0 ? (
          <p className="ap-empty">You don't have any assigned projects yet.</p>
        ) : (
          <>
            {/* Desktop/Tablet Table View */}
            <div className="admin-projects-table-container hide-mobile">
              <table className="admin-projects-table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th className="hide-md">Client</th>
                    <th className="hide-md">Type</th>
                    <th>Status</th>
                    <th className="hide-lg">Phase</th>
                    <th>Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {assignedFullProjects.map((project) => (
                    <ProjectRow
                      key={project.id}
                      project={project}
                      onClick={handleProjectClick}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Accordion View */}
            <div className="admin-projects-accordion show-mobile">
              {assignedFullProjects.map((project) => (
                <MobileAccordionItem
                  key={project.id}
                  project={project}
                  isExpanded={expandedAccordionId === project.id}
                  onToggle={() =>
                    setExpandedAccordionId(
                      expandedAccordionId === project.id ? null : project.id,
                    )
                  }
                  onNavigate={handleProjectClick}
                />
              ))}
            </div>
          </>
        )}
      </Section>

      <Section
        title="My Activity"
        icon={<FaClipboardList />}
        subtitle="Review your 15 most recent actions in the portal"
      >
        {activityLogs.length === 0 ? (
          <p className="ap-empty">
            Your activity will appear here as you use the portal.
          </p>
        ) : (
          <ul className="ap-activity-list">
            {activityLogs.map((log) => (
              <li key={log.id} className="ap-activity-item">
                <span className="ap-activity-icon">
                  {ENTITY_ICON[log.entityType] || <FaFileAlt />}
                </span>
                <div className="ap-activity-body">
                  <div className="ap-activity-action">
                    <strong>{log.action}</strong>
                    {log.entityName ? (
                      <span className="ap-activity-entity">
                        : {log.entityName}
                      </span>
                    ) : null}
                  </div>
                  <div className="ap-activity-time">
                    {formatLogTimestamp(log.timestamp)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

export default AdminProfile;
